/** @jsxImportSource @emotion/react */
import { DownOutlined, MenuFoldOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import {
  Breadcrumb,
  Button,
  Dropdown,
  Layout,
  Menu,
  Modal,
  Space,
  Spin,
} from "antd";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Route, Switch, withRouter } from "react-router-dom";
import Icon from "src/components/Icon";
import Login from "src/containers/login/Login";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { UserAuthoritiyEnum } from "src/models/license";
import { menus } from "src/routes/menu";
import { routers } from "src/routes/router";
import { LicenseSeviceClient } from "src/services/LicenseService";
import { supportedLocales } from "src/services/LocaleService";
import { getLocale, setLocale } from "src/utils/locale";
import "./AppLayout.css";

const { Header, Content, Sider } = Layout;
const { SubMenu } = Menu;

export default React.memo(
  withRouter(function AppLayout(props: any) {
    const formatMessage = useFormatMessage();
    const selectedLocale = getLocale();
    const [collapsed, setCollapsed] = useState(false);
    const [roles, setRoles] = useState<UserAuthoritiyEnum[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectKeys, setSelectKeys] = useState<string[]>([]);
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [keys, setKeys] = useState<{ subKey?: string; key: string }>();
    const [toHome, setToHome] = useState(false);

    useEffect(() => {
      setIsLoading(true);
      LicenseSeviceClient.userRole().then((res) => {
        setIsLoading(false);
        if (res.data.data.userAuthorities?.length > 0) {
          setRoles(res.data.data.userAuthorities);
        } else {
          Modal.warning({
            title: formatMessage(messageIds.common.alertTitle),
            content: formatMessage(messageIds.common.noPermission),
          });
        }
      });
    }, [formatMessage]);

    const onCollapse = useCallback(() => {
      setCollapsed(!collapsed);
    }, [collapsed]);

    const menuCss = css`
      border: none;
      color: #ffffff;
      background-color: transparent;
      overflow-x: hidden;
      overflow-y: auto;
      &::-webkit-scrollbar {
        width: 5px;
      }
      .ant-menu,
      .ant-menu-submenu-open {
        color: #ffffff;
        background-color: transparent;
      }
      .ant-menu-item,
      .ant-menu-submenu-title {
        height: 48px !important;
        line-height: 48px !important;
        padding-left: 30px !important;
        margin-top: 0px;
        margin-bottom: 0px !important;
      }
      .ant-menu-submenu {
        padding-bottom: 0;
        background: unset;
      }

      /* .ant-menu-item span:nth-of-type(2) {
        margin-left: 0px;
      } */
      /* .ant-menu-submenu-title span:nth-of-type(2) span {
        margin-left: 0px;
      } */
      .ant-menu-item-only-child {
        padding-left: 54px !important;
      }

      .ant-menu-inline {
        border: none;
      }
      .ant-menu-submenu-title,
      .ant-menu-inline .ant-menu-item {
        width: 100%;
        margin: 0px auto;
      }
      .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
        color: #ffffff;
        background-color: #3377ff !important;
      }
      .ant-menu-item-selected {
        color: #ffffff;
        font-weight: 600;
        background-color: #3377ff !important;
      }

      .ant-menu-item-active {
        color: #ffffff;
        background-color: #153687;
      }
      .ant-menu-inline .ant-menu-item-selected::after {
        border: none !important;
      }
      .ant-menu-item:active,
      .ant-menu-submenu-title:active {
        color: #3377ff;
        background-color: #153687;
      }
      .ant-menu-submenu-title:hover {
        background-color: #153687;
      }
      .ant-menu-submenu-arrow {
        &::before,
        &::after {
          background-image: linear-gradient(
            to right,
            #ffffff,
            #ffffff
          ) !important;
        }
      }
      .ant-menu-submenu-selected {
        color: #ffffff;
        background-color: unset;
      }
    `;

    const handleClick = useCallback(
      (params) => {
        if (params.key !== selectedLocale) {
          setLocale(params.key);
        }
      },
      [selectedLocale]
    );

    const menu = (
      <Menu onClick={handleClick}>
        {supportedLocales &&
          supportedLocales.map((locale) => (
            <Menu.Item key={locale.config.locale}>{locale.title}</Menu.Item>
          ))}
      </Menu>
    );

    const onHandleClick = useCallback((e) => {
      setSelectKeys(e.key);
      if (e.keyPath.length > 1) {
        const openKey = e.keyPath.filter((item: string) => item !== e.key);
        setOpenKeys(openKey);
        setKeys({
          subKey: openKey[0],
          key: e.key,
        });
      } else {
        setKeys({
          subKey: "",
          key: e.key,
        });
      }
    }, []);

    const { location, history } = props;

    const extraBreadcrumbItems = useMemo(() => {
      if (keys) {
        if (keys.subKey !== "") {
          const subMenu = menus.filter(
            (menu) => menu.subKey === keys.subKey
          )[0];

          const menuItem = subMenu?.children?.filter(
            (item) => item.key === keys.key
          )[0];

          const subMenuName = subMenu?.name as string;
          const menuItemName = menuItem?.name as string;

          return [
            <Breadcrumb.Item key={subMenu?.subKey}>
              {subMenuName && formatMessage(subMenuName)}
            </Breadcrumb.Item>,
            <Breadcrumb.Item key={menuItem?.key}>
              <span
                css={css`
                  color: #1890ff;
                `}
              >
                {menuItemName && formatMessage(menuItemName)}
              </span>
            </Breadcrumb.Item>,
          ];
        } else {
          const key = menus.filter((menu) => menu.key === keys.key)[0];
          return (
            <Breadcrumb.Item key={key?.key}>
              <span
                css={css`
                  color: #1890ff;
                `}
              >
                {key?.name && formatMessage(key.name)}
              </span>
            </Breadcrumb.Item>
          );
        }
      } else {
        return "";
      }
    }, [formatMessage, keys]);

    const breadcrumbItems =
      toHome === false && extraBreadcrumbItems ? (
        [
          <Breadcrumb.Item key="home">
            {formatMessage(messageIds.home.name)}
          </Breadcrumb.Item>,
        ].concat(extraBreadcrumbItems)
      ) : (
        <Breadcrumb.Item key="home">
          {formatMessage(messageIds.home.name)}
        </Breadcrumb.Item>
      );

    const navigationToHome = useCallback(() => {
      setKeys({
        subKey: "",
        key: "",
      });
      setOpenKeys([]);
      setSelectKeys([]);
      setToHome(true);
      history.push("/");
    }, [history]);

    const navigationToMenu = useCallback(
      (item, licenseType) => {
        setToHome(false);
        if (licenseType) {
          history.push({
            pathname: item,
            search: `?licenseType=${licenseType}`,
          });
        } else {
          history.push(item);
        }
      },
      [history]
    );

    useEffect(() => {
      menus.forEach((menu) => {
        if (menu.key) {
          if (menu.path === location.pathname) {
            setSelectKeys([menu.key]);
            setKeys({
              subKey: "",
              key: menu.key,
            });
          }
        } else {
          if (menu.subKey) {
            if (menu.type && location.pathname.includes(menu.type)) {
              setOpenKeys([menu.subKey]);
            }

            if (menu.children) {
              menu.children.forEach((child) => {
                if (
                  child.path === location.pathname &&
                  location.search.endsWith(child.search)
                ) {
                  setSelectKeys([child.key]);
                  setKeys({
                    subKey: menu.subKey,
                    key: child.key,
                  });
                }
              });
            }
          }
        }
      });
    }, [location.pathname, location.search]);

    return (
      <Layout
        style={{
          minHeight: "100vh",
        }}
      >
        <Sider
          collapsed={collapsed}
          css={css`
            display: flex;
            flex-direction: column;
            color: white;
            background: linear-gradient(
              178deg,
              rgb(20, 48, 102) 0%,
              rgb(20, 31, 51) 100%
            );
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              height: 60px;
              color: white;
            `}
            onClick={navigationToHome}
          >
            <Icon
              css={css`
                font-size: 26px;
              `}
              name="icon-logo"
            />
            <span
              css={css`
                font-size: 16px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              {!collapsed && formatMessage(messageIds.menu.name)}
            </span>
          </div>
          {isLoading ? (
            <Spin
              css={css`
                display: flex;
                justify-content: center;
                margin-top: 50px;
                .ant-spin-dot-item {
                  background-color: #ffffff;
                }
              `}
            />
          ) : (
            <Menu
              mode="inline"
              expandIcon={({ isOpen }) => {
                return (
                  <Icon
                    name="icon-menu-pull"
                    css={css`
                      position: absolute;
                      top: 50%;
                      margin-top: -4px;
                      right: 16px;
                      font-size: 8px;
                      color: #ffffff;
                      transform: rotate(${isOpen ? "180deg" : "0deg"});
                    `}
                  />
                );
              }}
              css={menuCss}
              defaultOpenKeys={openKeys}
              selectedKeys={selectKeys}
              onClick={onHandleClick}
            >
              {menus.map((menu) => (
                <Fragment key={menu.name}>
                  {roles.includes(UserAuthoritiyEnum[menu.userAuthoritiy]) &&
                    (menu.subKey ? (
                      <SubMenu
                        key={menu.subKey}
                        icon={<menu.iconType />}
                        title={formatMessage(menu.name)}
                      >
                        {menu.children?.map((item) => (
                          <Menu.Item
                            key={item.key}
                            onClick={() =>
                              navigationToMenu(item.path, item.search)
                            }
                          >
                            {formatMessage(item.name)}
                          </Menu.Item>
                        ))}
                      </SubMenu>
                    ) : (
                      <Menu.Item
                        key={menu.key}
                        icon={<menu.iconType />}
                        onClick={() => navigationToMenu(menu.path, menu.search)}
                      >
                        {formatMessage(menu.name)}
                      </Menu.Item>
                    ))}
                </Fragment>
              ))}
            </Menu>
          )}
        </Sider>
        <Layout
          css={css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            min-width: 0px;
            min-height: 0px;
          `}
        >
          <Header
            css={css`
              display: flex;
              justify-content: space-between;
              min-width: 800px;
              min-height: 50px;
              height: 50px;
              padding-left: 20px;
              padding-right: 70px;
              background-color: white;
              box-shadow: rgb(143 155 179 / 20%) 0px 1px 11px 0px;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <Button
                css={css`
                  display: flex;
                  align-items: center;
                  padding: 0;
                `}
                type="link"
                onClick={onCollapse}
              >
                <MenuFoldOutlined
                  css={css`
                    margin-right: 20px;
                    font-size: 20px;
                    line-height: 20px;
                    color: #3377ff;
                    transform: rotate(${collapsed ? "180deg" : "0deg"});
                  `}
                />
              </Button>
              <Breadcrumb separator="|">{breadcrumbItems}</Breadcrumb>
            </div>
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <div
                css={css`
                  margin-right: 40px;
                `}
              >
                <Dropdown overlay={menu}>
                  <Button
                    css={css`
                      color: #3d4966 !important;
                      font-size: 14px;
                      padding: 0;
                      border: none;
                      background: none;
                    `}
                  >
                    {
                      supportedLocales.find(
                        (l) => l.config.locale === getLocale()
                      )?.title
                    }
                    <DownOutlined />
                  </Button>
                </Dropdown>
              </div>
              <Space align="center" size={20}>
                <Login />
              </Space>
            </div>
          </Header>
          <Content
            css={css`
              display: flex;
              flex-grow: 1;
              min-width: 800px;
              min-height: 200px;
              position: relative;
              margin-top: 6px;
              margin-left: 8px;
              background-color: white;
              box-shadow: rgb(61 73 102 / 20%) 0px 0px 6px 0px;
              overflow: auto;
            `}
          >
            <div style={{ width: "100%" }}>
              <Switch>
                {Object.entries(routers).map(([key, route]) => (
                  <Route
                    key={key}
                    path={route.path}
                    component={route.component}
                    exact
                  ></Route>
                ))}
              </Switch>
            </div>
          </Content>
        </Layout>
      </Layout>
    );
  })
);
